import React, { useMemo, useState } from 'react';
import { FaPlus, FaTimes, FaCalculator, FaChevronDown, FaEdit } from 'react-icons/fa';
import {
  buildFormulaFieldConfig,
  getFormulaDefinition,
  getFormulaOperandLists,
  listFormulaDefinitions,
  listFormulaSourceOptions,
  FORMULA_KEYS,
  FORMULA_SOURCE_KINDS,
  decodeFormulaSourceKey,
  encodeFormulaSourceKey,
} from '../../utils/formulaFields';
import { useReportGroupPicklistValues } from './matrix/useReportGroupPicklistValues';
import './css/FormulaFieldsSection.css';

function mergePicklistOntoField(field, picklistByFieldId) {
  if (!field) return field;
  const id = String(field.id || field.field_config_id || field.field_id || '');
  const pl = id ? picklistByFieldId?.[id] : null;
  if (!pl?.length) return field;
  return { ...field, picklist_values: pl, picklist_values_data: pl };
}

function OperandList({
  title,
  hint,
  rows,
  onChange,
  sourceOptions,
}) {
  const updateRow = (index, value) => {
    const next = [...rows];
    next[index] = value;
    onChange(next);
  };

  const removeRow = (index) => {
    if (rows.length <= 1) return;
    onChange(rows.filter((_, i) => i !== index));
  };

  const addRow = () => onChange([...rows, '']);

  return (
    <div className="form-group formula-operand-group">
      <label>
        {title} <span className="formula-operand-sum-hint">(summed)</span>
      </label>
      {hint && <small className="form-text formula-operand-group-hint">{hint}</small>}
      {rows.map((value, index) => (
        <div key={`${title}-${index}`} className="formula-operand-row">
          <select
            className="form-control"
            value={value}
            onChange={(e) => updateRow(index, e.target.value)}
          >
            <option value="">Select field…</option>
            {sourceOptions.map((o) => (
              <option key={`${title}-${index}-${o.value}`} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="remove-operand-btn"
            onClick={() => removeRow(index)}
            disabled={rows.length <= 1}
            title="Remove"
          >
            <FaTimes />
          </button>
        </div>
      ))}
      <button type="button" className="add-operand-btn" onClick={addRow}>
        <FaPlus /> Add field
      </button>
    </div>
  );
}

const FormulaFieldsSection = ({
  formulaFields = [],
  setFormulaFields,
  selectedFields = [],
  summarizedFields = {},
  selectedGroups = [],
  selectedColumnGroups = [],
  reportFields = [],
  collapsed = false,
  onToggleCollapse,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFieldName, setEditingFieldName] = useState(null);
  const [selectedFormulaKey, setSelectedFormulaKey] = useState(FORMULA_KEYS.PERIOD_OVER_PERIOD_GROWTH);
  const [selectedSourceValue, setSelectedSourceValue] = useState('');
  const [numeratorOperands, setNumeratorOperands] = useState(['']);
  const [denominatorOperands, setDenominatorOperands] = useState(['']);
  const [customLabel, setCustomLabel] = useState('');

  const definitions = listFormulaDefinitions();

  const groupFieldsForPicklist = useMemo(
    () => [...(selectedGroups || []), ...(selectedColumnGroups || [])],
    [selectedGroups, selectedColumnGroups]
  );

  const reportFieldsForPicklist = useMemo(
    () => [...(selectedFields || []), ...(reportFields || [])],
    [selectedFields, reportFields]
  );

  const picklistByFieldId = useReportGroupPicklistValues(
    groupFieldsForPicklist,
    reportFieldsForPicklist
  );

  const enrichedFieldsForFormulas = useMemo(() => {
    const merge = (f) => mergePicklistOntoField(f, picklistByFieldId);
    const all = [
      ...(selectedFields || []).map(merge),
      ...(selectedGroups || []).map(merge),
      ...(selectedColumnGroups || []).map(merge),
      ...(reportFields || []).map(merge),
    ];
    const seen = new Set();
    return all.filter((f) => {
      const k = `${f.object_name || 'main'}:${f.field_name}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [
    selectedFields,
    selectedGroups,
    selectedColumnGroups,
    reportFields,
    picklistByFieldId,
  ]);

  const sourceOptions = useMemo(
    () =>
      listFormulaSourceOptions(
        selectedFields,
        summarizedFields,
        selectedGroups,
        selectedColumnGroups,
        enrichedFieldsForFormulas
      ),
    [
      selectedFields,
      summarizedFields,
      selectedGroups,
      selectedColumnGroups,
      enrichedFieldsForFormulas,
    ]
  );

  const rowGroup = selectedGroups?.[0] || null;
  const def = definitions.find((d) => d.key === selectedFormulaKey);
  const isWinRate = selectedFormulaKey === FORMULA_KEYS.WIN_RATE;
  const showCustomLabel = def?.supportsCustomLabel !== false;

  const findSourceOption = (value) =>
    sourceOptions.find((o) => o.value === value) ||
    (() => {
      const decoded = decodeFormulaSourceKey(value);
      if (!decoded?.fieldName) return null;
      return { value, ...decoded, label: decoded.fieldName };
    })();

  const closeModal = () => {
    setShowAddModal(false);
    setEditingFieldName(null);
  };

  const operandKeysFromFormula = (formulaField) => {
    const { numerators, denominators } = getFormulaOperandLists(formulaField);
    return {
      numerators: numerators.map((s) => encodeFormulaSourceKey(s)).filter(Boolean),
      denominators: denominators.map((s) => encodeFormulaSourceKey(s)).filter(Boolean),
    };
  };

  const growthSourceKeyFromFormula = (formulaField) => {
    if (!formulaField?.source_field_name) return '';
    const key = encodeFormulaSourceKey({
      sourceKind: FORMULA_SOURCE_KINDS.MEASURE,
      fieldName: formulaField.source_field_name,
      objectName: formulaField.source_object_name,
      aggregation: formulaField.source_aggregation || 'sum',
    });
    if (sourceOptions.some((o) => o.value === key)) return key;
    const match = sourceOptions.find(
      (o) =>
        o.fieldName === formulaField.source_field_name &&
        (o.objectName || 'main') === (formulaField.source_object_name || 'main')
    );
    return match?.value || key;
  };

  const handleOpenAdd = () => {
    setEditingFieldName(null);
    setSelectedFormulaKey(FORMULA_KEYS.PERIOD_OVER_PERIOD_GROWTH);
    setSelectedSourceValue(sourceOptions[0]?.value || '');
    setNumeratorOperands([sourceOptions[0]?.value || '']);
    setDenominatorOperands([sourceOptions[1]?.value || sourceOptions[0]?.value || '']);
    setCustomLabel('');
    setShowAddModal(true);
  };

  const handleOpenEdit = (formulaField) => {
    if (!formulaField) return;
    setEditingFieldName(formulaField.field_name);
    setSelectedFormulaKey(formulaField.formula_key || FORMULA_KEYS.PERIOD_OVER_PERIOD_GROWTH);

    if (formulaField.formula_key === FORMULA_KEYS.WIN_RATE) {
      const { numerators, denominators } = operandKeysFromFormula(formulaField);
      setNumeratorOperands(numerators.length ? numerators : ['']);
      setDenominatorOperands(denominators.length ? denominators : ['']);
      setCustomLabel(formulaField.field_label || 'Win Rate %');
      setSelectedSourceValue('');
    } else {
      setSelectedSourceValue(growthSourceKeyFromFormula(formulaField));
      setCustomLabel(formulaField.field_label || '');
      setNumeratorOperands(['']);
      setDenominatorOperands(['']);
    }
    setShowAddModal(true);
  };

  const handleFormulaTypeChange = (key) => {
    setSelectedFormulaKey(key);
    if (key === FORMULA_KEYS.WIN_RATE) {
      setCustomLabel('Win Rate %');
      setNumeratorOperands([sourceOptions[0]?.value || '']);
      setDenominatorOperands([sourceOptions[1]?.value || '']);
    } else if (key === FORMULA_KEYS.PERIOD_OVER_PERIOD_GROWTH) {
      setCustomLabel('Growth %');
      setSelectedSourceValue(sourceOptions[0]?.value || '');
    } else {
      setCustomLabel('');
      setSelectedSourceValue(sourceOptions[0]?.value || '');
    }
  };

  const isEditing = Boolean(editingFieldName);
  const editingFormula = isEditing
    ? formulaFields.find((f) => f.field_name === editingFieldName)
    : null;

  const handleSave = () => {
    if (isWinRate) {
      const numOpts = numeratorOperands
        .map((v) => findSourceOption(v))
        .filter(Boolean);
      const denOpts = denominatorOperands
        .map((v) => findSourceOption(v))
        .filter(Boolean);

      if (!numOpts.length || !denOpts.length) {
        alert('Add at least one numerator and one denominator field.');
        return;
      }
      if (!String(customLabel || '').trim()) {
        alert('Enter a label for this formula column.');
        return;
      }

      const next = buildFormulaFieldConfig({
        formulaKey: FORMULA_KEYS.WIN_RATE,
        customLabel: customLabel.trim(),
        groupFieldName: rowGroup?.field_name || null,
        groupFieldLabel: rowGroup?.field_label || null,
        numeratorSources: numOpts,
        denominatorSources: denOpts,
        id: editingFormula?.id || null,
      });

      if (!next) return;
      if (
        formulaFields.some(
          (f) =>
            f.field_name === next.field_name && f.field_name !== editingFieldName
        )
      ) {
        alert('A Win Rate % formula with these operands already exists.');
        return;
      }

      if (isEditing) {
        setFormulaFields(
          formulaFields.map((f) =>
            f.field_name === editingFieldName ? next : f
          )
        );
      } else {
        setFormulaFields([...formulaFields, next]);
      }
      closeModal();
      return;
    }

    const source = findSourceOption(selectedSourceValue);
    if (!source) {
      alert('Select a field to use in the formula.');
      return;
    }
    if (!rowGroup && def?.requiresRowGroup) {
      alert('Add at least one row group (e.g. Close Date by Month) before using Growth %.');
      return;
    }

    const next = buildFormulaFieldConfig({
      formulaKey: selectedFormulaKey,
      sourceFieldName: source.fieldName,
      sourceFieldLabel: source.fieldLabel,
      sourceObjectName: source.objectName,
      sourceAggregation: source.aggregation,
      groupFieldName: rowGroup?.field_name || null,
      groupFieldLabel: rowGroup?.field_label || null,
      customLabel: String(customLabel || '').trim() || null,
      id: editingFormula?.id || null,
    });

    if (!next) return;

    if (
      formulaFields.some(
        (f) => f.field_name === next.field_name && f.field_name !== editingFieldName
      )
    ) {
      alert('This formula column already exists for that measure.');
      return;
    }

    if (isEditing) {
      setFormulaFields(
        formulaFields.map((f) =>
          f.field_name === editingFieldName ? next : f
        )
      );
    } else {
      setFormulaFields([...formulaFields, next]);
    }
    closeModal();
  };

  const handleRemove = (fieldName) => {
    setFormulaFields(formulaFields.filter((f) => f.field_name !== fieldName));
  };

  const canAddFormula = sourceOptions.length > 0;
  const canSubmitWinRate =
    numeratorOperands.every((v) => v) &&
    denominatorOperands.every((v) => v) &&
    String(customLabel || '').trim();
  const canSubmitGrowth = Boolean(selectedSourceValue);

  return (
    <>
      <div className="formula-fields-section">
        <div
          className="section-header clickable"
          onClick={onToggleCollapse}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && onToggleCollapse()}
        >
          <h4>
            <FaCalculator className="section-icon" /> Formula Fields
            <span className="field-count">({formulaFields.length})</span>
          </h4>
          <span className={`collapse-icon ${collapsed ? 'collapsed' : ''}`}>
            <FaChevronDown />
          </span>
        </div>

        {!collapsed && (
          <div className="formula-fields-body">
            <p className="formula-fields-hint">
              Prebuilt calculations on report data (Growth %, Win Rate %, and more).
            </p>

            {formulaFields.length > 0 && (
              <ul className="formula-fields-list">
                {formulaFields.map((ff) => {
                  const ffDef = getFormulaDefinition(ff.formula_key);
                  return (
                    <li key={ff.id || ff.field_name} className="formula-field-item">
                      <div className="formula-field-info">
                        <strong>{ff.field_label}</strong>
                        <small>{ffDef?.expression || ff.formula_key}</small>
                      </div>
                      <div className="formula-field-actions">
                        <button
                          type="button"
                          className="edit-formula-btn"
                          onClick={() => handleOpenEdit(ff)}
                          title="Edit formula"
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="remove-formula-btn"
                          onClick={() => handleRemove(ff.field_name)}
                          title="Remove formula"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <button
              type="button"
              className="add-formula-btn"
              onClick={handleOpenAdd}
              disabled={!canAddFormula}
            >
              <FaPlus /> Add formula field
            </button>
            {!canAddFormula && (
              <small className="formula-fields-hint warn">
                Add at least one column to the report (summarized measure or detail field).
              </small>
            )}
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="formula-modal-overlay" onClick={closeModal}>
          <div className="formula-modal" onClick={(e) => e.stopPropagation()}>
            <div className="formula-modal-header">
              <h3>{isEditing ? 'Edit formula field' : 'Add formula field'}</h3>
              <button type="button" className="close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            <div className="formula-modal-body">
              <div className="form-group">
                <label>Formula</label>
                <select
                  className="form-control"
                  value={selectedFormulaKey}
                  onChange={(e) => handleFormulaTypeChange(e.target.value)}
                  disabled={isEditing}
                >
                  {definitions.map((d) => (
                    <option key={d.key} value={d.key}>
                      {d.label}
                    </option>
                  ))}
                </select>
                {def && (
                  <small className="form-text">
                    {def.description}
                    <br />
                    <code>{def.expression}</code>
                  </small>
                )}
              </div>

              {showCustomLabel && (
                <div className="form-group">
                  <label>Column label</label>
                  <input
                    type="text"
                    className="form-control"
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                    placeholder={isWinRate ? 'e.g. Win Rate %' : 'e.g. Growth %'}
                  />
                  <small className="form-text">
                    Stored on the report and shown as the column header.
                  </small>
                </div>
              )}

              {isWinRate ? (
                <>
                  <small className="form-text formula-win-rate-hint">
                    Example: Win (8) + Lost (2) = 10 → Win Rate = 8 ÷ 10 × 100 = 80%.
                    Add multiple fields on each side; values are summed per row.
                  </small>
                  <OperandList
                    title="Numerator"
                    hint="e.g. Count — Closed Won"
                    rows={numeratorOperands}
                    onChange={setNumeratorOperands}
                    sourceOptions={sourceOptions}
                  />
                  <OperandList
                    title="Denominator"
                    hint="e.g. Count — all Stage (total)"
                    rows={denominatorOperands}
                    onChange={setDenominatorOperands}
                    sourceOptions={sourceOptions}
                  />
                </>
              ) : (
                <div className="form-group">
                  <label>Calculate on</label>
                  <select
                    className="form-control"
                    value={selectedSourceValue}
                    onChange={(e) => setSelectedSourceValue(e.target.value)}
                  >
                    <option value="">Select field…</option>
                    {sourceOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {rowGroup && !isWinRate && (
                <small className="form-text">
                  Compared along row group:{' '}
                  <strong>{rowGroup.field_label || rowGroup.field_name}</strong>
                </small>
              )}
              {rowGroup && isWinRate && (
                <small className="form-text">
                  Calculated per row group:{' '}
                  <strong>{rowGroup.field_label || rowGroup.field_name}</strong>
                </small>
              )}
            </div>

            <div className="formula-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={isWinRate ? !canSubmitWinRate : !canSubmitGrowth}
              >
                {isEditing ? 'Save' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FormulaFieldsSection;
